"use client";

import { useLayoutEffect, useRef, useState } from "react";

import cn from "classnames";

import { wrapReviewQuote } from "./adtCustomerReviewsSection.utils";

export function AdtCustomerReviewQuote({ quote }: AdtCustomerReviewQuoteProps) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const quoteRef = useRef<HTMLParagraphElement>(null);
  const displayQuote = wrapReviewQuote(quote);

  useLayoutEffect(() => {
    const node = quoteRef.current;
    if (!node) return;

    const updateOverflow = () => {
      if (expanded) return;
      setIsOverflowing(node.scrollHeight > node.clientHeight + 1);
    };

    updateOverflow();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(node);
    return () => observer.disconnect();
  }, [displayQuote, expanded]);

  if (!displayQuote) return null;

  const showMore = isOverflowing && !expanded;

  return (
    <div className="flex flex-col items-start gap-2">
      <p
        ref={quoteRef}
        data-testid="customer-review-quote"
        className={cn("body-4 text-greyscale-body m-0", {
          "line-clamp-8": !expanded,
        })}>
        {displayQuote}
      </p>
      {showMore ? (
        <button
          type="button"
          className="text-primary body-4 focus-visible:ring-secondary cursor-pointer rounded-sm underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          data-testid="customer-review-show-more"
          onClick={() => setExpanded(true)}>
          Show more
        </button>
      ) : null}
    </div>
  );
}

type AdtCustomerReviewQuoteProps = {
  quote: string;
};
